import { TemplateResult } from 'lit';
import { getVisibleShellNavItems } from '../../../../../app-context/domain/shellNavItems.ts';
import type { CoreShellPorts } from '../../../../../routing/domain/CoreShellPorts.ts';
import { reportShellNavPermissionLoadFailure } from '../../../shellNavPermissionSync.ts';
import { fetchShellPermissionSet, onShellPendingSyncCountChanged, onShellRouteChange } from '../../../shellUi.ts';
import { LitComponent } from './LitComponent.ts';
import { sidebarComponentTemplate } from './sidebarComponentTemplate.ts';

export class SidebarComponent extends LitComponent {
  shellPorts!: CoreShellPorts;

  private currentPath: string = '/';
  /** `null` until first resolve: routes without module permission only. */
  private permissionSet: ReadonlySet<string> | null = null;
  private pendingSyncCount = 0;
  private offShellRoute?: () => void;
  private offPendingSyncCount?: () => void;
  /** Connected callback. */


  connectedCallback(): void {
    if (!this.shellPorts) {
      throw new Error('sidebar-component: falta shellPorts (debe ir bajo layout-component)');
    }
    super.connectedCallback();
    this.refreshPendingSyncCount();
    this.updateCurrentPath();
    this.syncNavPermissionsSafe();
  }
  /** Setup event listeners. */


  protected setupEventListeners(): void {
    super.setupEventListeners();
    this.offShellRoute = onShellRouteChange(() => {
      this.syncNavPermissionsSafe();
      this.updateCurrentPath();
      this.refreshPendingSyncCount();
    });
    this.offPendingSyncCount = onShellPendingSyncCountChanged(() => this.refreshPendingSyncCount());
  }
  /** Remove event listeners. */


  protected removeEventListeners(): void {
    this.offShellRoute?.();
    this.offPendingSyncCount?.();
    super.removeEventListeners();
  }
  /** Sync nav permissions safe. */


  private syncNavPermissionsSafe(): void {
    void this.syncNavPermissions().catch((err) => {
      reportShellNavPermissionLoadFailure(
        this.shellPorts,
        'sidebar.syncNav',
        'No se pudieron cargar los permisos del menú.',
        err,
      );
      this.permissionSet = new Set();
      this.update();
    });
  }
  /** Sync nav permissions. */


  private async syncNavPermissions(): Promise<void> {
    this.permissionSet = await fetchShellPermissionSet(this.shellPorts.auth);
    this.update();
  }
  /** Update current path. */


  private refreshPendingSyncCount(): void {
    const next = this.shellPorts.pendingSyncBadge.getPendingUploadRecordCount();
    if (this.pendingSyncCount !== next) {
      this.pendingSyncCount = next;
      this.update();
    }
  }

  private updateCurrentPath(): void {
    const newPath = globalThis.location.pathname;
    if (this.currentPath !== newPath) {
      this.currentPath = newPath;
      this.update();
    }
  }
  /** On nav click. */


  private onNavClick(e: Event, path: string): void {
    e.preventDefault();
    this.shellPorts.navigation.navigate(path);
    setTimeout(() => this.updateCurrentPath(), 0);
  }
  /** Returns template. */


  protected getTemplate(): TemplateResult {
    const navItems = getVisibleShellNavItems(this.permissionSet);
    const activePath = globalThis.location.pathname;
    return sidebarComponentTemplate({
      navItems,
      activePath,
      pendingSyncCount: this.pendingSyncCount,
      onNavClick: (e, path) => this.onNavClick(e, path),
    });
  }
}

customElements.define('sidebar-component', SidebarComponent);
